import { existsSync, readFileSync, readdirSync, statSync } from "node:fs";
import { createHash } from "node:crypto";
import { join } from "node:path";

const VERSION_FILE = join(process.cwd(), ".generated", "site-asset-version.json");

/** @param {string[]} parts */
function hashParts(parts) {
  return createHash("sha256").update(parts.join("\0")).digest("hex");
}

/** @type {string | null} */
let cachedVersion = null;
/** @type {number} */
let cachedDevMtime = 0;

function readBuiltVersionFile() {
  if (!existsSync(VERSION_FILE)) return null;
  try {
    const parsed = JSON.parse(readFileSync(VERSION_FILE, "utf8"));
    const v = String(parsed?.v || "").trim();
    return v || null;
  } catch {
    return null;
  }
}

function computeDevVersion() {
  const rels = ["src/styles/global.css", "public/.generated/ll-deferred-global.css"];
  const jsDir = join(process.cwd(), "public", "js");
  if (existsSync(jsDir)) {
    for (const name of readdirSync(jsDir).filter((f) => f.endsWith(".js")).sort()) {
      rels.push(`public/js/${name}`);
    }
  }
  const lines = [];
  for (const rel of rels) {
    const abs = join(process.cwd(), rel);
    if (!existsSync(abs)) {
      lines.push(`${rel}:missing`);
      continue;
    }
    const st = statSync(abs);
    lines.push(`${rel}:${st.mtimeMs}:${st.size}`);
  }
  return hashParts(lines).slice(0, 12);
}

/**
 * Short hash used as `?v=` on same-origin JS/CSS URLs.
 * @returns {string}
 */
export function getSiteAssetVersion() {
  if (process.env.NODE_ENV === "development") {
    const now = Date.now();
    if (!cachedVersion || now - cachedDevMtime > 1500) {
      cachedVersion = computeDevVersion();
      cachedDevMtime = now;
    }
    return cachedVersion || "dev";
  }
  if (cachedVersion) return cachedVersion;
  cachedVersion = readBuiltVersionFile() || computeDevVersion() || "0";
  return cachedVersion;
}

/**
 * @param {string} href
 * @returns {string}
 */
export function versionPublicAssetUrl(href) {
  const raw = String(href || "").trim();
  if (!raw) return raw;
  if (/^(https?:|data:|\/\/)/i.test(raw)) return raw;
  if (!/^\/(?:js\/[^?]+\.js|\.generated\/[^?]+\.css)(?:\?|$)/.test(raw)) return raw;
  if (/\bv=/.test(raw)) return raw;
  const version = getSiteAssetVersion();
  const sep = raw.includes("?") ? "&" : "?";
  return `${raw}${sep}v=${encodeURIComponent(version)}`;
}

/**
 * Append `?v=` to same-origin `/js/*.js` and `/.generated/*.css` in rendered HTML.
 * @param {string} html
 * @returns {string}
 */
export function injectVersionedAssetUrls(html) {
  const version = getSiteAssetVersion();
  if (!version || !html) return html;
  return html.replace(
    /((?:src|href)=["'])(\/(?:js\/[^"'?]+\.js|\.generated\/[^"'?]+\.css))(?=["'])/g,
    `$1$2?v=${version}`,
  );
}
